/**
 * Rules Engine Service
 * Frontend service for rules engine API calls
 * Spec reference: Rules_Engine_Functional_Spec.md, Section 7
 */

import { apiClient, APIResponse } from './client';
import {
  Rule,
  RuleCreate,
  RuleUpdate,
  EvaluateRequest,
  EvaluateResponse,
  ConflictResult,
} from '../../types/rulesEngine';

interface RuleWithConflicts {
  rule: Rule;
  conflicts: ConflictResult[];
}

interface RuleListResponse {
  rules: Rule[];
  total: number;
}

interface ConflictCheckResponse {
  conflicts: ConflictResult[];
}

class RulesEngineService {
  /**
   * List all rules for a client, with optional filters.
   * Spec: Section 7.2
   */
  async listRules(
    clientId: string,
    context?: string,
    isActive?: boolean
  ): Promise<APIResponse<RuleListResponse>> {
    const params = new URLSearchParams();
    if (context) params.set('context', context);
    if (isActive !== undefined) params.set('isActive', String(isActive));
    const qs = params.toString();
    return apiClient.get<RuleListResponse>(
      `/api/v1/rules/${clientId}${qs ? `?${qs}` : ''}`
    );
  }

  /**
   * Get a single rule by ID.
   * Spec: Section 7.3
   */
  async getRule(
    clientId: string,
    ruleId: string
  ): Promise<APIResponse<{ rule: Rule }>> {
    return apiClient.get<{ rule: Rule }>(
      `/api/v1/rules/${clientId}/${ruleId}`
    );
  }

  /**
   * Create a new rule.
   * Spec: Section 7.4
   */
  async createRule(
    clientId: string,
    rule: RuleCreate
  ): Promise<APIResponse<RuleWithConflicts>> {
    return apiClient.post<RuleWithConflicts>(
      `/api/v1/rules/${clientId}`,
      rule
    );
  }

  /**
   * Update an existing rule (partial update).
   * Spec: Section 7.5
   */
  async updateRule(
    clientId: string,
    ruleId: string,
    rule: RuleUpdate
  ): Promise<APIResponse<RuleWithConflicts>> {
    return apiClient.put<RuleWithConflicts>(
      `/api/v1/rules/${clientId}/${ruleId}`,
      rule
    );
  }

  /**
   * Delete a rule.
   * Spec: Section 7.6
   */
  async deleteRule(
    clientId: string,
    ruleId: string
  ): Promise<APIResponse<void>> {
    return apiClient.delete<void>(
      `/api/v1/rules/${clientId}/${ruleId}`
    );
  }

  /**
   * Evaluate an entity against all active rules for a context.
   * Spec: Section 7.7
   */
  async evaluate(
    clientId: string,
    request: EvaluateRequest
  ): Promise<APIResponse<EvaluateResponse>> {
    return apiClient.post<EvaluateResponse>(
      `/api/v1/rules/${clientId}/evaluate`,
      request
    );
  }

  /**
   * Check a rule definition for conflicts without saving.
   * Spec: Section 7.8
   */
  async checkConflicts(
    clientId: string,
    rule: RuleCreate
  ): Promise<APIResponse<ConflictCheckResponse>> {
    return apiClient.post<ConflictCheckResponse>(
      `/api/v1/rules/${clientId}/check-conflicts`,
      rule
    );
  }
}

export const rulesEngineService = new RulesEngineService();
